import { useQuery } from "@tanstack/react-query";
import { fetchProducts, fetchProductByHandle, type ShopifyProduct } from "@/lib/shopify";

/**
 * Returns true if the product has at least one variant currently in stock /
 * available for sale. Used across all archives to hide out-of-stock pieces
 * (which, per the project's business rules, are pulled from inventory rather
 * than marked as sold).
 */
export function isProductAvailable(product: ShopifyProduct): boolean {
  return product.variants.edges.some((v) => v.node.availableForSale);
}

interface UseProductsOptions {
  /**
   * When true (default) products with zero inventory across all variants are
   * filtered out. Pass `false` from pages like /sold that intentionally want
   * to display unavailable items.
   */
  availableOnly?: boolean;
}

export function useProducts(
  count: number = 10,
  query?: string,
  options: UseProductsOptions = {}
) {
  const { availableOnly = true } = options;
  return useQuery({
    queryKey: ["shopify-products", count, query, availableOnly],
    queryFn: async () => {
      const products = await fetchProducts(count, query);
      if (!availableOnly) return products;
      return products.filter(isProductAvailable);
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useProductByHandle(handle: string) {
  return useQuery({
    queryKey: ["shopify-product", handle],
    queryFn: () => fetchProductByHandle(handle),
    enabled: !!handle,
    staleTime: 1000 * 60 * 5,
  });
}
